/**
 * StepFreeText — wizard step 1.1 (Phase 1).
 *
 * "Je pars de zéro" path: user describes themselves in free text.
 * AI parses it and pre-fills subsequent wizard steps.
 * Skippable — user can proceed without filling anything.
 */

import { useState } from 'react';
import { Loader2, Sparkles, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { Textarea } from '../../ui/textarea';
import type { CVData } from '../../../types';
import type { CommonInfo } from '../../../hooks/useCvState';
import { COMMON_FIELDS } from '../../../hooks/useCvEditing';

interface StepFreeTextProps {
  onExtracted: (data: CVData, commonInfo: Partial<CommonInfo>) => void;
  goNext: () => void;
}

type ParseState = 'idle' | 'loading' | 'done' | 'error';

const PLACEHOLDER = `Exemple :
Je m'appelle Marie Dupont, développeuse web fullstack avec 5 ans d'expérience chez Accenture et Leboncoin.
Maîtrise React, Node.js, PostgreSQL, Docker.
Formation : Master Informatique à Paris-Saclay (2019).
Langues : Français natif, Anglais courant.
Je cherche un poste de Lead Developer à Paris.`;

export function StepFreeText({ onExtracted, goNext }: StepFreeTextProps) {
  const [text, setText] = useState('');
  const [state, setState] = useState<ParseState>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleExtract = async () => {
    if (!text.trim()) return;
    setState('loading');
    setError(null);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ route: 'parse', text: text.trim() }),
      });
      const json = await res.json() as { data?: CVData; error?: string };
      if (!res.ok || !json.data) {
        throw new Error(json.error || "Erreur lors de l'analyse");
      }
      const cvData: CVData = {
        ...json.data,
        personalInfo: json.data.personalInfo ?? {},
        experience:   json.data.experience   ?? [],
        education:    json.data.education    ?? [],
        skills:       json.data.skills       ?? [],
        languages:    json.data.languages    ?? [],
        hobbies:      json.data.hobbies      ?? [],
      };

      // Extract CommonInfo fields from the parsed data
      const parsedCommon: Partial<CommonInfo> = {};
      for (const field of COMMON_FIELDS) {
        const val = (cvData.personalInfo as Record<string, unknown>)[field];
        if (typeof val === 'string' && val) {
          (parsedCommon as Record<string, unknown>)[field] = val;
        }
      }

      setState('done');
      // Short delay so user sees success feedback before advancing
      setTimeout(() => {
        onExtracted(cvData, parsedCommon);
        goNext();
      }, 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setState('error');
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-blue-500/20 text-blue-400 mb-2">
          <Sparkles className="size-6" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Parlez-nous de vous</h1>
        <p className="text-base text-white/60 max-w-xl mx-auto">
          Décrivez votre parcours en quelques lignes (nom, poste, expériences, compétences…).
          L'IA va extraire les informations et pré-remplir les étapes suivantes.
        </p>
      </div>

      <div className="space-y-3">
        <Textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setState('idle'); setError(null); }}
          placeholder={PLACEHOLDER}
          rows={10}
          className="resize-none bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-blue-500/50 text-sm leading-relaxed"
          disabled={state === 'loading' || state === 'done'}
        />
        <p className="text-xs text-white/40">
          Rien à mettre si vous préférez remplir manuellement — cliquez sur "Passer".
        </p>
      </div>

      {/* Error */}
      {state === 'error' && error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Success */}
      {state === 'done' && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm">
          <CheckCircle2 className="size-4 shrink-0" />
          Extraction réussie — remplissage automatique en cours…
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={goNext}
          className="text-sm text-white/50 hover:text-white/70 transition-colors underline underline-offset-4"
        >
          Passer cette étape →
        </button>
        <button
          type="button"
          onClick={handleExtract}
          disabled={!text.trim() || state === 'loading' || state === 'done'}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-600/20 transition-all disabled:opacity-40 disabled:pointer-events-none"
        >
          {state === 'loading' ? (
            <><Loader2 className="size-4 animate-spin" /> Extraction en cours…</>
          ) : state === 'done' ? (
            <><CheckCircle2 className="size-4" /> Extrait !</>
          ) : (
            <><Sparkles className="size-4" /> Extraire avec l'IA <ArrowRight className="size-4" /></>
          )}
        </button>
      </div>
    </div>
  );
}
